import { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { deviceSpecsApi } from '../../api'
import type { DeviceSpecDetail, DeviceSpecTag, EdgeDevice, EdgeTag } from '../../types'
import { addressKey } from '../deviceSpec/pullMerge'

// 이 서버에서 고른 명세서에서 Edge 태그와 같은 태그 찾기 — 디바이스 코드 + 주소로 짝짓는다
// (주소는 Edge → 명세 가져오기와 같은 규칙: 대소문자·띄어쓰기·숫자 앞 0 무시).
// 마스터데이터 항목 코드 기본값 = {설비코드}_{Alias} 의 Alias 를 Edge 태그 Alias 가 비었을 때 명세에서 대신 찾는다.
// Edge 태그 Description 은 메모라 쓰지 않는다.

export type SpecTagOf = (tagId: string) => DeviceSpecTag | null

export function specTagLookup(devices: EdgeDevice[] | undefined, spec: DeviceSpecDetail | undefined): SpecTagOf | null {
  if (!devices || !spec) return null
  const byTagId = new Map<string, DeviceSpecTag>()
  for (const d of devices) {
    const specDevice = spec.devices.find(s => s.code.trim() === d.code)
    if (!specDevice) continue
    const byAddress = new Map<string, DeviceSpecTag>()
    for (const t of specDevice.tags) {
      const key = addressKey(specDevice.channelType, t.address)
      if (key && !byAddress.has(key)) byAddress.set(key, t)
    }
    for (const t of d.tags) {
      const match = t.address ? byAddress.get(addressKey(specDevice.channelType, t.address)) : undefined
      if (match) byTagId.set(t.id, match)
    }
  }
  return tagId => byTagId.get(tagId) ?? null
}

/** 태그 Alias — Edge 태그 Alias 가 있으면 그것, 없으면 명세 Alias (둘 다 비면 '') */
export function tagAlias(tag: EdgeTag | null | undefined, specTag: DeviceSpecTag | null): string {
  return tag?.alias?.trim() || specTag?.alias.trim() || ''
}

/** 항목 코드 기본값 — 태그 Alias(Edge 먼저, 없으면 명세)가 있으면 {설비코드}_{Alias}, 없으면 null */
export function aliasItemCode(machineCode: string, tag: EdgeTag | null | undefined, specTag: DeviceSpecTag | null): string | null {
  const alias = tagAlias(tag, specTag)
  return alias ? `${machineCode}_${alias}` : null
}

// 이 서버에서 마지막으로 고른 명세서 — [디바이스] → [명세 → Edge 적용]·[마스터데이터] → [명세]에서 고르면 기억한다 (브라우저별).
// 서버와 명세서를 묶어 두지는 않는다 — 고를 때마다 바꿀 수 있고, 다음에 열 때 처음 고른 값으로만 쓴다
const storageKey = (serverId: string) => `edge:serverSpec:${serverId}`

function readSpecId(serverId: string): string | null {
  try { return localStorage.getItem(storageKey(serverId)) } catch { return null }
}

/** 이 서버에서 마지막으로 고른 명세서. 고른 적이 없거나(또는 지워졌거나) enabled 가 아니면 spec 은 undefined */
export function useServerSpec(serverId: string, enabled = true) {
  // 고른 값은 서버별 — 다른 서버로 바뀌면 그 서버의 기억을 다시 읽는다
  const [picked, setPicked] = useState(() => ({ serverId, id: readSpecId(serverId) }))
  const stored = picked.serverId === serverId ? picked.id : readSpecId(serverId)

  const setSpecId = useCallback((id: string | null) => {
    try {
      if (id) localStorage.setItem(storageKey(serverId), id)
      else localStorage.removeItem(storageKey(serverId))
    } catch { /* 저장 못 해도 이번 화면에선 쓴다 */ }
    setPicked({ serverId, id })
  }, [serverId])

  const spec = useQuery({
    queryKey: ['device-specs', stored],
    queryFn: () => deviceSpecsApi.get(stored!).then(r => r.data),
    enabled: enabled && !!stored,
    retry: false,
  })
  // 지워진 명세서면 잊는다
  const missing = spec.isError && (spec.error as { response?: { status?: number } })?.response?.status === 404
  useEffect(() => {
    if (missing) try { localStorage.removeItem(storageKey(serverId)) } catch { /* 무시 */ }
  }, [missing, serverId])

  const specId = missing ? null : stored
  return { specId, setSpecId, spec: specId ? spec.data : undefined }
}
